//import React from 'react';
import { FaqSection } from './FaqSection/FaqSection';
import Newsletter from '../../layouts/Newsletter/Newsletter';
import { RelatedBreadcrumbLinks } from './RelatedBreadcrumbLinks/RelatedBreadcrumbLinks';
import { AttractionTopTips } from './AttractionTopTips/AttractionTopTips';
import { useEffect, useRef, useState } from 'react';
import { ThingsToDO } from '../../types/pages/things-to-do';
import { TopTip } from '../Attraction/topTip';
import Image from 'next/image';
import { LoadingSpinner } from '../../shared-components/LoadingSpinner/LoadingSpinner';
import { Header } from './Header/Header';
import { useMediaQuery } from '../../hooks/mediaQuery';
import { MobileView } from './MobileView/MobileView';
import { Map } from './Map/Map';
import {
  AttractionCardType,
  EAttractionCardPageType,
  ESectionInView
} from '../../types/components/attraction-card';
import Pagination from '../../shared-components/Pagination/Pagination';
import scrollToElement from '../../utils/scrollIntoView';
import { useRouter } from 'next/router';
import AttractionCard from '../../shared-components/AttractionCard/AttractionCard';

interface ThingsToDoProps {
  thingsToDoDetails: ThingsToDO;
  topTips: TopTip[];
}

export const ThingsToDo = ({ thingsToDoDetails, topTips }: ThingsToDoProps) => {
  const isMobileView = useMediaQuery(639);
  const [citySlug, setCitySlug] = useState('');
  const [zoomLevel, setZoomLevel] = useState(13);
  const [attractionData, setAttractionData] = useState<AttractionCardType[]>();
  const [showFullMap, setShowFullMap] = useState(false);
  const [radius, setRadius] = useState<number>(5);
  const [inViewSection, setInViewSection] = useState<ESectionInView>(
    ESectionInView.LIST
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedAttractionData, setPaginatedAttractionData] = useState<
    AttractionCardType[]
  >([]);
  const [hoveredAttractionId, setHoveredAttractionId] = useState<string>('');
  const attractionDivRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (attractionId: string) => {
    setHoveredAttractionId(attractionId);
  };
  const handleMouseLeave = () => {
    setHoveredAttractionId('');
  };
  const mapRef: any = useRef(null);

  const itemsPerPage = 18;

  useEffect(() => {
    setCurrentPage(1);
  }, [zoomLevel]);

  const router = useRouter();
  const { slug } = router.query;

  useEffect(() => {
    if (slug) setCitySlug(slug as string);
  }, [slug as string]);

  const toggleSection = () => {
    if (ESectionInView.LIST === inViewSection) {
      setInViewSection(ESectionInView.MAP);
      setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 0);
    } else {
      setInViewSection(ESectionInView.LIST);
    }
  };

  // Get Paginated Attractions Data based on zoom in zoom out
  useEffect(() => {
    const getSlicedAttractionData = () => {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return attractionData?.slice(startIndex, endIndex);
    };

    setPaginatedAttractionData(getSlicedAttractionData() || []);
  }, [attractionData, currentPage]);

  const totalPages =
    (attractionData && Math.ceil(attractionData.length / itemsPerPage)) || 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (!isMobileView) {
      scrollToElement('attractions-card');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (attractionDivRef.current) {
        attractionDivRef.current.scrollTop = 0;
      }
    }
  }, [currentPage, inViewSection]);

  return (
    <div className="h-full">
      <Header
        citySlug={citySlug as string}
        cityName={thingsToDoDetails?.city?.name}
      />

      {isMobileView ? (
        <MobileView
          attractionData={attractionData}
          setAttractionData={setAttractionData}
          showFullMap={true}
          thingsToDoDetails={thingsToDoDetails}
          topTips={topTips}
          setRadius={setRadius}
          radius={radius}
          mapRef={mapRef}
          paginatedAttractionData={paginatedAttractionData}
          currentPage={currentPage}
          attractionsPerPage={itemsPerPage}
          handlePageChange={handlePageChange}
        />
      ) : (
        <>
          <div className="flex flex-col sm:gap-[45px] xl:gap-[90px] xxsm:hidden sm:flex pb-20">
            <div
              className={`bg-bluishGray flex ${
                inViewSection === ESectionInView.LIST && !showFullMap
                  ? totalPages > 1
                    ? `pb-[55px]`
                    : `pb-14 sm:pb-14 mlg:pb-[98px]`
                  : showFullMap
                  ? 'pb-0'
                  : 'pb-0'
              }
                relative`}
            >
              <div
                className={`${
                  showFullMap
                    ? 'hidden'
                    : inViewSection == ESectionInView.LIST
                    ? 'w-full sm:w-full flex flex-col mlg:w-[55%] xl:w-[62%] 3xl:w-[55%]'
                    : 'sm:hidden mlg:flex mlg:w-[55%] xl:w-[62%] 3xl:w-[55%]'
                } `}
              >
                <div className="w-full">
                  {attractionData !== undefined && (
                    <p className="text-lg leading-7 font-normal py-11  sm:px-8 md:px-16 mlg:px-7 3xl:px-14">
                      {`Almost ${attractionData?.length} Attraction${
                        attractionData?.length == 1 ? '' : 's'
                      } within map area`}
                    </p>
                  )}
                  <div
                    className="thin-scrollbar overflow-y-scroll transition-all h-[750px]"
                    id="attractions-card"
                    ref={attractionDivRef} // Reference to the scrollable div
                  >
                    {attractionData === undefined ? (
                      <div className="flex items-center justify-center h-[550px] flex-col gap-10">
                        <LoadingSpinner className="w-8 h-8" />
                      </div>
                    ) : paginatedAttractionData.length === 0 ? (
                      <div className="flex items-center justify-center h-[550px] flex-col gap-10">
                        <Image
                          src="https://res.cloudinary.com/see-sight-tours/image/upload/v1709532363/Remaining%20images/Attraction/ekhp3i11sp3dsr7e2poi.png"
                          alt="no-attractions-image"
                          width={300}
                          height={300}
                        />
                        <p className="text-base font-normal">
                          Currently no attractions available
                        </p>
                      </div>
                    ) : (
                      <div
                        className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 mmd:grid-cols-3 lg:grid-cols-3 mlg:grid-cols-2 xl:grid-cols-3 gap-[18px] sm:px-8 md:px-12 mlg:pr-7 xl:pr-4 3xl:px-10 3xl:pr-5
                  `}
                      >
                        {paginatedAttractionData.map((cardDetails, index) => (
                          <div
                            key={index}
                            onMouseEnter={() =>
                              handleMouseEnter(cardDetails.id)
                            }
                            onMouseLeave={handleMouseLeave}
                          >
                            <AttractionCard
                              attractionCard={cardDetails}
                              key={cardDetails.id}
                              index={
                                (currentPage - 1) * itemsPerPage + index + 1
                              }
                              isMapCard={false}
                              pageType={
                                EAttractionCardPageType.THINGS_TODO_PAGE
                              }
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {totalPages > 1 && (
                    <Pagination
                      totalPages={totalPages}
                      currentPage={currentPage}
                      handlePageChange={page => handlePageChange(page)}
                    />
                  )}
                </div>
              </div>
              <Map
                hoveredAttractionId={hoveredAttractionId}
                attractionData={attractionData}
                setAttractionData={setAttractionData}
                showFullMap={showFullMap}
                setShowFullMap={setShowFullMap}
                thingsToDoDetails={thingsToDoDetails}
                setRadius={setRadius}
                radius={radius}
                sectionInView={inViewSection}
                mapRef={mapRef}
                pageType={EAttractionCardPageType.THINGS_TODO_PAGE}
              />

              <div
                className={`absolute z-[999] sm:flex ${
                  inViewSection === ESectionInView.LIST
                    ? paginatedAttractionData.length === 0
                      ? `sm:top-[37rem]`
                      : `sm:top-[50rem]`
                    : `sm:bottom-0 sm:mb-[6rem]`
                }  mlg:hidden left-1/2 transform -translate-x-1/2 transition-all duration-500 ease-in-out`}
              >
                <button
                  className={` 
                       bg-[#222] text-white text-base leadin-[18px] px-5 py-2.5 rounded-full flex items-center h-[42px]`}
                  onClick={toggleSection}
                >
                  <Image
                    src={
                      inViewSection == ESectionInView.LIST
                        ? '/map-icon.svg'
                        : '/list-icon.svg'
                    }
                    alt="map-icon"
                    width={18}
                    height={18}
                    className="mr-2"
                  />

                  {inViewSection == ESectionInView.LIST
                    ? 'Show Map'
                    : 'Show List'}
                </button>
              </div>
            </div>

            {/* <TrendingIdeas trending={thingsToDoDetails?.trending} />

            <TopTours thingsToDoDetails={thingsToDoDetails} />

            <NeedToKnow
              description={thingsToDoDetails.need_to_know}
              cardItems={thingsToDoDetails.need_to_know_items}
            /> */}

            {topTips && <AttractionTopTips topTips={topTips} />}

            {thingsToDoDetails.faqs && (
              <FaqSection faqs={thingsToDoDetails.faqs} />
            )}

            {thingsToDoDetails?.related_urls && (
              <RelatedBreadcrumbLinks
                breadcrumbs={thingsToDoDetails.related_urls}
              />
            )}

            <Newsletter />
          </div>
        </>
      )}
    </div>
  );
};
